import axios from 'axios';
import Constants from '../constants';
import { DistributionFileUploadType, DistributionType, ExternalApplicationType, IDistributionFile } from '../models';
import { IFileUploadResponse } from '../models/IFileUploadResponse';
import { AccessCodeType } from '../models/AccessCodeType';
import { SsoAttribute } from '../models/SsoAttribute';

export function getDistributionsQuery(
	surveyId: number,
	audienceId: number,
	onlyValidate?: boolean,
	fileUploadType?: DistributionFileUploadType,
	deleteBeforeUpload?: boolean,
	distributionType?: number,
) {
	let params = `${Constants.SURVEY_BUILDER_SURVEYS_API_URL}/${surveyId}/audiences/${audienceId}/distributions`;
	if (fileUploadType) {
		params += `?fileUploadType=${fileUploadType}`;
	}
	if (onlyValidate) {
		if (params.includes('?')) {
			params += `&onlyValidate=${onlyValidate}`;
		} else {
			params += `?onlyValidate=${onlyValidate}`;
		}
	}
	if (deleteBeforeUpload) {
		if (params.includes('?')) {
			params += `&deleteBeforeUpload=${deleteBeforeUpload}`;
		} else {
			params += `?deleteBeforeUpload=${deleteBeforeUpload}`;
		}
	}
	if (distributionType && distributionType !== 0) {
		if (params.includes('?')) {
			params += `&distributionType=${distributionType}`;
		} else {
			params += `?distributionType=${distributionType}`;
		}
	}
	return params;
}

export function getValidateFileQuery(
	surveyId: number,
	audienceId: number,
	fileUploadType?: DistributionFileUploadType,
	distributionType?: number,
	codeType?: AccessCodeType,
	ssoAttribute?: SsoAttribute,
) {
	let params = `${Constants.SURVEY_BUILDER_SURVEYS_API_URL}/${surveyId}/audiences/${audienceId}/distributions/validateFile`;
	if (fileUploadType) {
		params += `?fileUploadType=${fileUploadType}`;
	}
	if (distributionType && distributionType !== 0) {
		if (params.includes('?')) {
			params += `&distributionType=${distributionType}`;
		} else {
			params += `?distributionType=${distributionType}`;
		}
	}
	if (codeType) {
		if (params.includes('?')) {
			params += `&codeType=${codeType}`;
		} else {
			params += `?codeType=${codeType}`;
		}
	}
	if (ssoAttribute) {
		if (params.includes('?')) {
			params += `&ssoAttribute=${ssoAttribute}`;
		} else {
			params += `?ssoAttribute=${ssoAttribute}`;
		}
	}
	return params;
}

export function getAddDistributionQuery(
	surveyId: number,
	audienceId: number,
	deleteBeforeUpload?: boolean,
	distributionType?: number,
	ssoAttribute?: SsoAttribute,
) {
	let params = `${Constants.SURVEY_BUILDER_SURVEYS_API_URL}/${surveyId}/audiences/${audienceId}/distributions/add`;
	if (deleteBeforeUpload) {
		params += `?deleteBeforeUpload=${deleteBeforeUpload}`;
	}
	if (distributionType && distributionType !== 0) {
		if (params.includes('?')) {
			params += `&distributionType=${distributionType}`;
		} else {
			params += `?distributionType=${distributionType}`;
		}
	}
	if (ssoAttribute) {
		if (params.includes('?')) {
			params += `&ssoAttribute=${ssoAttribute}`;
		} else {
			params += `?ssoAttribute=${ssoAttribute}`;
		}
	}
	return params;
}

export function getAppendDistributionQuery(surveyId: number, audienceId: number, distributionType?: number) {
	let params = `${Constants.SURVEY_BUILDER_SURVEYS_API_URL}/${surveyId}/audiences/${audienceId}/distributions/append`;
	if (distributionType && distributionType !== 0) {
		params += `?distributionType=${distributionType}`;
	}
	return params;
}

export function getDeleteDistributionQuery(surveyId: number, audienceId: number, force?: boolean) {
	let params = `${Constants.SURVEY_BUILDER_SURVEYS_API_URL}/${surveyId}/audiences/${audienceId}/distributions`;
	if (force) {
		params += '?force=true';
	}
	return params;
}

export function getArchivedDistributionsOfAudienceQuery(surveyId: number, audienceId: number) {
	return `${Constants.SURVEY_BUILDER_SURVEYS_API_URL}/${surveyId}/audiences/${audienceId}/distributions/archive`;
}

export function getDownloadListQuery(surveyId: number, audienceId: number) {
	const url = Constants.DISTRIBUTION_LIST_DOWNLOAD.replace('{surveyId}', surveyId.toString()).replace(
		'{audienceId}',
		audienceId.toString(),
	);
	return url;
}

export function getDownloadTemplateQuery(distributionType: DistributionType, applicationType?: ExternalApplicationType) {
	const url = Constants.DISTRIBUTION_TEMPLATE_DOWNLOAD.replace('{distributionType}', distributionType.toString()).replace(
		'{externalApplicationType}',
		applicationType ? applicationType.toString() : '',
	);
	return url;
}

export async function getDistributions(surveyId: number, audienceId: number): Promise<IDistributionFile[]> {
	const res = await axios.get(getDistributionsQuery(surveyId, audienceId), Constants.noCacheConfig);
	return res.data;
}

export async function ValidateFile(
	surveyId: number,
	audienceId: number,
	fileUploadType: DistributionFileUploadType,
	file: any,
	distributionType: number,
	codeType?: AccessCodeType,
	ssoAttribute?: SsoAttribute,
): Promise<IDistributionFile> {
	const data = new FormData();
	data.append('file', file);
	const res = await axios.post(getValidateFileQuery(surveyId, audienceId, fileUploadType, distributionType, codeType, ssoAttribute), data);
	return res.data;
}

export async function AddDistribution(
	surveyId: number,
	audienceId: number,
	file: any,
	deleteBeforeUpload: boolean,
	distributionType: number,
	ssoAttribute?: SsoAttribute,
): Promise<IFileUploadResponse> {
	const data = new FormData();
	data.append('file', file);
	const res = await axios.post(getAddDistributionQuery(surveyId, audienceId, deleteBeforeUpload, distributionType, ssoAttribute), data);
	return res.data;
}

export async function AppendDistribution(
	surveyId: number,
	audienceId: number,
	file: any,
	distributionType: number,
): Promise<IFileUploadResponse> {
	const data = new FormData();
	data.append('file', file);
	const res = await axios.put(getAppendDistributionQuery(surveyId, audienceId, distributionType), data);
	return res.data;
}

export async function deleteDistribution(surveyId: number, audienceId: number, force: boolean): Promise<IDistributionFile> {
	const res = await axios.delete(getDeleteDistributionQuery(surveyId, audienceId, force));
	return res.data;
}

export async function archiveDistributionsOfAudience(surveyId: number, audienceId: number): Promise<any> {
	const res = await axios.put(getArchivedDistributionsOfAudienceQuery(surveyId, audienceId));
	return res.data;
}

export async function downloadDistributionList(surveyId: number, audienceId: number): Promise<any> {
	const response = await axios.get(getDownloadListQuery(surveyId, audienceId), {
		responseType: Constants.FileDownloadResponseType,
		timeout: Constants.FileDownloadTimeout,
		headers: Constants.excelResponseHeader,
	});
	return response.data;
}

export async function downloadDistributionTemplate(
	distributionType: DistributionType,
	applicationType?: ExternalApplicationType,
): Promise<any> {
	const res = await axios.get(getDownloadTemplateQuery(distributionType, applicationType), {
		responseType: Constants.FileDownloadResponseType,
		timeout: Constants.FileDownloadTimeout,
		headers: Constants.excelResponseHeader,
	});
	return res.data;
}
